import Box from '@mui/material/Box';
import { type ReactElement, type ReactNode } from 'react';

interface ReadOnlyFieldsetProps {
    // When true, everything inside becomes uneditable and looks it.
    readOnly?: boolean;
    // Padding, in theme spacing units. The shared Dialog wants 2; a dialog that already pads its own
    // DialogContent wants 0, which is the default.
    pad?: number;
    children: ReactNode;
}

// Makes a block of form controls read-only, once, instead of threading a flag through every field.
//
// This was the shared Dialog's private implementation until the permission sweep found that most dialogs in
// the app are raw MUI ones that never had it (TTD-159): the grid's edit button correctly turned into an eye
// for a user with read access only, and then opened a fully editable dialog with a working Save button. The
// affordance was cosmetic. Extracted here so the seven of them get the real thing rather than seven
// approximations of it.
//
// A disabled fieldset disables every native control inside it, and that is what actually enforces read-only.
// Anything that must stay usable — tab bars, Cancel/Close — belongs OUTSIDE it. A tab bar placed inside is
// exactly the bug Dialog.test.tsx pins: the tabs go dead and the user can only ever see whichever one
// happened to be open.
const ReadOnlyFieldset = ({ readOnly, pad = 0, children }: ReadOnlyFieldsetProps): ReactElement => (
    <Box
        component="fieldset"
        disabled={readOnly}
        sx={{
            p: pad, m: 0, border: 0, minWidth: 0,
            // The fieldset disables the native controls; this makes them LOOK disabled. MUI styles from its
            // own `disabled` prop and never learns about an ancestor fieldset, so without this the fields sit
            // at full opacity and the ButtonBase wrapper around every checkbox and switch keeps its hover,
            // focus and ripple while the input inside it is already inert. Uneditable, but not visibly so,
            // and still apparently clickable.
            //
            // Keyed off `&:disabled` rather than the readOnly prop so the styling cannot drift away from what
            // the fieldset is actually doing.
            '&:disabled': {
                '& .MuiFormLabel-root, & .MuiInputBase-input, & .MuiFormControlLabel-label': {
                    color: 'text.disabled',
                    WebkitTextFillColor: 'unset',
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'action.disabledBackground' },
                // Not cosmetic: without this the wrapper still takes the pointer, so a control that cannot be
                // changed still lights up as though it can.
                '& .MuiButtonBase-root': { pointerEvents: 'none', color: 'action.disabled' },
            },
        }}
    >
        {children}
    </Box>
);

export default ReadOnlyFieldset;
